import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';

import { ApiService } from '@services/api.service';
import { environment } from '@env/environment';

import {
  BillItemModel,
  BillItemPendingModel,
  BillModel,
  BillQueryParamsModel,
  BillSearchModel,
  PaymentMethodModel,
} from '@data-layer/models/payments';
import { PaginatedResultModel } from '@data-layer/models/common';
import { enumRespErrors } from '../enums/common';
import { map } from 'rxjs/operators';
import { enumBillItemProductCode } from '@data-layer/enums/payments';
import { groupCollectionFees } from '@app/shared/helpers';
import { isDefined } from '@services/utils/utils.service';

@Injectable({
  providedIn: 'root',
})
export class BillsRepository {
  constructor(private apiService: ApiService) {}

  public search(companyId: number, params?: BillSearchModel, isShowLoader?: boolean): Observable<PaginatedResultModel<BillModel>> {
    return this.apiService.get<PaginatedResultModel<BillModel>>(
      `${environment.url.companies}${companyId}/bills`,
      {
        params,
      },
      { isShowLoader },
    );
  }

  public getById(companyId: number, billId: number, isShowLoader?: boolean): Observable<BillModel> {
    return this.apiService.get<BillModel>(`${environment.url.prefixCustoms}v2/bills/${billId}`, null, {
      isShowLoader,
    });
  }

  public searchAll(params?: BillSearchModel, isShowLoader?: boolean): Observable<PaginatedResultModel<BillModel>> {
    return this.apiService
      .get<PaginatedResultModel<BillModel>>(
        `${environment.url.prefixCustoms}v2/bills`,
        {
          params,
        },
        { isShowLoader },
      )
      .pipe(
        map((data: PaginatedResultModel<BillModel>) => {
          return groupCollectionFees<BillModel, BillItemModel>(data);
        }),
      );
  }

  public create(companyId: number, query: BillQueryParamsModel): Observable<BillModel[]> {
    const params: BillQueryParamsModel = {
      referenceNumbers: query.referenceNumbers,
      ...(isDefined(query?.paymentProvider) && { paymentProvider: query.paymentProvider }),
      ...(isDefined(query?.paymentCodeId) && { paymentCodeId: query.paymentCodeId }),
      ...(isDefined(query?.paymentMethodId) && { paymentMethodId: query.paymentMethodId }),
      ...(isDefined(query?.billToId) && { billToId: query.billToId }),
    };

    if (query.coupon) {
      params.coupon = query.coupon;
    }
    return this.apiService.post<BillModel[]>(`${environment.url.companiesV3}${companyId}/bills`, params);
  }

  public pendingBills(companyId: number, query: BillQueryParamsModel, isShowLoader: boolean = true): Observable<BillItemPendingModel[]> {
    const params: BillQueryParamsModel = {
      ...(isDefined(query?.referenceNumbers) && { referenceNumbers: query.referenceNumbers }),
      ...(isDefined(query?.paymentProvider) && { paymentProvider: query.paymentProvider }),
      ...(isDefined(query?.paymentCodeId) && { paymentCodeId: query.paymentCodeId }),
      ...(isDefined(query?.paymentMethodId) && { paymentMethodId: query.paymentMethodId }),
      ...(!!query?.coupon && { coupon: query.coupon }),
      isCalculateFee: Boolean(query?.isCalculateFee),
    };

    return this.apiService
      .get<BillItemPendingModel[]>(
        `${environment.url.companiesV3}${companyId}/pending-bills`,
        {
          params,
        },
        { isShowLoader },
      )
      .pipe(
        map((data) => {
          // #5442 if discount applied & no item with productCode throw an error
          if (!!query.coupon && !data.find((item) => item.productCode === enumBillItemProductCode.DISCOUNT)) {
            throw { error: { tag: enumRespErrors.DiscountCannotBeApplied } };
          }
          return data;
        }),
      );
  }

  public canceledBills(companyId: number, query: BillQueryParamsModel, isShowLoader: boolean = true): Observable<BillItemPendingModel[]> {
    const params: BillQueryParamsModel = {
      referenceNumbers: query.referenceNumbers,
      ...(isDefined(query?.paymentProvider) && { paymentProvider: query.paymentProvider }),
      ...(isDefined(query?.paymentCodeId) && { paymentCodeId: query.paymentCodeId }),
      ...(isDefined(query?.paymentMethodId) && { paymentMethodId: query.paymentMethodId }),
      ...(!!query?.coupon && { coupon: query.coupon }),
    };

    return this.apiService
      .get<BillItemPendingModel[]>(
        `${environment.url.companiesV3}${companyId}/cancelled-bills`,
        {
          params,
        },
        { isShowLoader },
      )
      .pipe(
        map((data) => {
          // #5442 if discount applied & no item with productCode throw an error
          if (!!query.coupon && !data.find((item) => item.productCode === enumBillItemProductCode.DISCOUNT)) {
            throw { error: { tag: enumRespErrors.DiscountCannotBeApplied } };
          }
          return data;
        }),
      );
  }

  public getErrorMessage(error: HttpErrorResponse): string {
    if (error && error.error) {
      switch (error.error.tag) {
        case enumRespErrors.DiscountUnauthorized:
          return 'DISCOUNT_IS_NOT_AUTHORISED';
        case enumRespErrors.DiscountIsNotActive:
          return 'DISCOUNT_IS_NOT_ACTIVE';
        case enumRespErrors.DiscountCannotBeApplied:
          return 'DISCOUNT_CANNOT_BE_APPLIED';
        case enumRespErrors.DiscountNotFound:
          return 'DISCOUNT_IS_NOT_FOUND';
        default:
          break;
      }
    }
  }

  public paymentMethods(companyId?: number, isShowLoader?: boolean): Observable<PaymentMethodModel[]> {
    return this.apiService.get<PaymentMethodModel[]>(
      `${environment.url.prefixCustoms}v2/payment-methods`,
      {
        ...(isDefined(companyId) && { params: { companyId } }),
      },
      { isShowLoader },
    );
  }

  public allPaymentMethods(isShowLoader?: boolean): Observable<PaymentMethodModel[]> {
    return this.apiService.get<PaymentMethodModel[]>(`${environment.url.prefixCustoms}v2/payment-methods/all`, null, { isShowLoader });
  }

  public createPaymentIntent(billId: number, offSession?: boolean, isShowLoader?: boolean): Observable<{ clientSecret: string }> {
    return this.apiService.post<{ clientSecret: string }>(
      `${environment.url.stripe}payment-intent`,
      null,
      {
        params: { billId, ...(isDefined(offSession) && { offSession }) },
      },
      { isShowLoader },
    );
  }

  public setupIntent(companyId: number, paymentCodeId: number): Observable<{ clientSecret: string }> {
    return this.apiService.post<{ clientSecret: string }>(`${environment.url.stripe}setup-intent`, null, {
      params: { companyId, paymentCodeId },
    });
  }

  public setupOrganizationIntent(organizationId: number, paymentCodeId: number): Observable<{ clientSecret: string }> {
    return this.apiService.post<{ clientSecret: string }>(`${environment.url.stripe}setup-intent`, null, {
      params: { organizationId, paymentCodeId },
    });
  }

  public paypalSetupToken(returnUrl: string, cancelUrl: string): Observable<{ approvalSessionId: string }> {
    return this.apiService.post<{ approvalSessionId: string }>(`${environment.url.paypalVault}setup-token`, {
      cancelUrl,
      returnUrl,
    });
  }

  public paypalPaymentToken(companyId: number, approvalSessionId: string): Observable<void> {
    return this.apiService.post<void>(`${environment.url.paypalVault}payment-token`, { companyId, approvalSessionId });
  }

  public paypalPaymentRemove(companyId: number): Observable<void> {
    return this.apiService.delete<void>(`${environment.url.paypalVault}payment-token`, {
      params: { companyId },
    });
  }

  public paypalOrganizationPaymentToken(organizationId: number, approvalSessionId: string): Observable<void> {
    return this.apiService.post<void>(`${environment.url.paypalVault}payment-token`, { organizationId, approvalSessionId });
  }

  public paypalOrganizationPaymentRemove(organizationId: number): Observable<void> {
    return this.apiService.delete<void>(`${environment.url.paypalVault}payment-token`, {
      params: { organizationId },
    });
  }
}
